import cloneDeep from 'lodash/cloneDeep';
import { useRef } from 'react';
import { message } from 'antd';
import { getFramework, getTargetType, useIdentity, useMoveEventListener } from '@/framework';
import { getLocaleStore } from '@/i18n';
import { ITouchKitEvent } from '@/utils/event-kit';
import { ILayerProps, MAX_STAGE_WIDTH as MAX_STAGE_SIZE, MIN_STAGE_WIDTH as MIN_STAGE_SIZE } from '@/version-types';
import { CropStagePlugin } from '../controller';
import { useStageCropStore } from '../models/crop-stage-state';
import { getOperateStageStyle, setScaleStageStyle } from '../utils';

const dataTypes = ['crop-rt', 'crop-lt', 'crop-rb', 'crop-lb'];

const adjustSize = (size: number) => {
    if (size < MIN_STAGE_SIZE) {
        return 50;
    } else if (size > MAX_STAGE_SIZE) {
        return MAX_STAGE_SIZE;
    }

    return size;
};

export const useScaleOperate = (scale: number) => {
    const identity = useIdentity();
    const framework = getFramework(identity);
    const originIndex = framework.stage.getActiveOriginIndex();
    const getLayers = () => framework.getLayers(originIndex);
    const getMainLayer = () => framework.getMainLayer(originIndex)!;
    const cropController = framework.getInstance(CropStagePlugin);

    const { $t } = getLocaleStore();

    const lastMainLayerRef = useRef<ILayerProps | null>(null);
    const lastLayersRef = useRef<ILayerProps[] | null>(null);
    const { setIsCropStageOperating } = useStageCropStore()((state) => state);
    const isStageAutoCrop = false;
    useMoveEventListener((e: ITouchKitEvent) => {
        const target = e.target;
        const dataType = target.getAttribute('data-type');

        if (!getTargetType(target)) return;

        if (!dataTypes.includes(dataType || '')) return;

        if (e.isStart) {
            const mainLayer = getMainLayer();
            const layers = getLayers();
            lastMainLayerRef.current = cloneDeep(mainLayer);
            lastLayersRef.current = cloneDeep(layers);
            console.log(
                'start===>',
                lastMainLayerRef.current.left,
                lastMainLayerRef.current.top,
                lastMainLayerRef.current.width,
                lastMainLayerRef.current.height,
            );
            return;
        }

        if (!lastMainLayerRef.current || !lastLayersRef.current || !dataType) {
            return;
        }
        setIsCropStageOperating(true);

        const { left, top, width, height } = lastMainLayerRef.current;
        const params = {
            width,
            height,
            left,
            top,
        };

        const { displacementX, displacementY } = e;
        const moveDistance = {
            x: displacementX / scale,
            y: displacementY / scale,
        };

        const radio = moveDistance.x / params.width;

        if (dataType === 'crop-lt') {
            const val = params.width - moveDistance.x;

            params.width = adjustSize(val);
            params.left = params.left - moveDistance.x - (val - params.width);
            console.log('params.left====', left, params.left, moveDistance.x - Math.abs(val - params.width));
            params.top = params.top - (params.height - (params.width / width) * height);
            params.height = (params.width / width) * height;

            if (params.height > MAX_STAGE_SIZE) {
                message.error($t((v) => v.stageManager.canvasLimitScope));
                params.top = params.top - (params.height - MAX_STAGE_SIZE);
                params.height = MAX_STAGE_SIZE;
                params.left = params.left - (params.width - (params.height / height) * width);
                params.width = (params.height / height) * width;
            }

            if (params.height < MIN_STAGE_SIZE) {
                message.error($t((v) => v.stageManager.canvasLimitScope));
                params.top = params.top - (params.height - MIN_STAGE_SIZE);
                params.height = MIN_STAGE_SIZE;
                params.left = params.left - (params.width - (params.height / height) * width);
                params.width = (params.height / height) * width;
            }
        } else if (dataType === 'crop-rt') {
            const val = params.width + moveDistance.x;
            params.width = adjustSize(val);

            params.top = params.top + params.height * radio;
            params.height = params.height + params.height * radio;

            if (params.height > MAX_STAGE_SIZE) {
                message.error($t((v) => v.stageManager.canvasLimitScope));
                params.top = params.top - (params.height - MAX_STAGE_SIZE);
                params.height = MAX_STAGE_SIZE;
                params.width = (params.height / height) * width;
            }

            if (params.height < MIN_STAGE_SIZE) {
                message.error($t((v) => v.stageManager.canvasLimitScope));
                params.top = params.top - (params.height - MIN_STAGE_SIZE);
                params.height = MIN_STAGE_SIZE;
                params.width = (params.height / height) * width;
            }
        } else if (dataType === 'crop-rb') {
            const val = params.width + moveDistance.x;
            params.width = adjustSize(val);

            params.height = params.height + params.height * radio;

            if (params.height > MAX_STAGE_SIZE) {
                message.error($t((v) => v.stageManager.canvasLimitScope));
                params.height = MAX_STAGE_SIZE;
                params.width = (params.height / height) * width;
            }

            if (params.height < MIN_STAGE_SIZE) {
                message.error($t((v) => v.stageManager.canvasLimitScope));
                params.height = MIN_STAGE_SIZE;
                params.width = (params.height / height) * width;
            }
        } else if (dataType === 'crop-lb') {
            const val = params.width - moveDistance.x;
            params.width = adjustSize(val);
            params.left = params.left - moveDistance.x - (val - params.width);

            params.height = params.height - params.height * radio;

            if (params.height > MAX_STAGE_SIZE) {
                message.error($t((v) => v.stageManager.canvasLimitScope));
                params.height = MAX_STAGE_SIZE;
                params.left = params.left - (params.width - (params.height / height) * width);
                params.width = (params.height / height) * width;
            }

            if (params.height < MIN_STAGE_SIZE) {
                message.error($t((v) => v.stageManager.canvasLimitScope));
                params.height = MIN_STAGE_SIZE;
                params.left = params.left - (params.width - (params.height / height) * width);
                params.width = (params.height / height) * width;
            }
        }

        console.log('params====', params.width, params.height, params.left, params.top);
        const newLayerSize = cropController.moveScaleStageSizeOperate(
            params,
            cloneDeep(lastMainLayerRef.current),
            cloneDeep(lastLayersRef.current),
            dataType,
            isStageAutoCrop,
        );

        if (newLayerSize) {
            const moveDirection = {
                x: e.displacementX ? e.displacementX / Math.abs(e.displacementX) : 1,
                y: e.displacementY ? e.displacementY / Math.abs(e.displacementY) : 1,
            };
            setScaleStageStyle(
                newLayerSize.width,
                newLayerSize.height,
                scale,
                dataType,
                isStageAutoCrop,
                moveDirection,
                {
                    width,
                    height,
                },
                e.isEnd,
            );
            getOperateStageStyle(
                newLayerSize.width,
                newLayerSize.height,
                scale,
                dataType,
                isStageAutoCrop,
                moveDirection,
                {
                    width,
                    height,
                },
                e.isEnd,
            );
        }

        if (e.isEnd) {
            setIsCropStageOperating(false);
        }
    });
};
